import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useSelector } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useGetTradingOfferOptions } from 'modules/trading/hooks/useGetTradingOfferOptions';
import { useGetTradingOfferOperations } from 'modules/trading/hooks/useGetTradingOfferOperations';
import { useGetTradingOfferOperationStatusOptions } from 'modules/trading/hooks/useGetTradingOfferOperationStatusOptions';
import { setModal } from 'modules/_shared/redux/modalActions';
import { EditTradingOfferOperationStatusModalProvider } from 'modules/trading/modals/EditTradingOfferOperationStatus/context/EditTradingOfferOperationStatusModalContext';
import EditTradingOfferOperationStatusModal from 'modules/trading/modals/EditTradingOfferOperationStatus';

import { useGetTradingOfferTypeOptions } from '../hooks/useGetTradingOfferTypeOptions';
import { TradingOfferOperationsContextType } from '../types/TradingOfferOperationsContextType';
import { tradingOfferOperationsInitValue } from '../constants/tradingOfferOperationsInitValue';

export const TradingOfferOperationsContext =
  createContext<TradingOfferOperationsContextType>(
    tradingOfferOperationsInitValue
  );

type TradingOfferOperationsProviderProps = {
  children: ReactNode;
  tradingOfferId?: string;
  partnerId?: string;
};

export const TradingOfferOperationsProvider: FC<
  TradingOfferOperationsProviderProps
> = ({ children, tradingOfferId, partnerId }) => {
  const history = useHistory();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );

  const tradingOfferOperations = useSelector(
    (state: State) => state.tradingOfferOperations
  );

  const partnerQuery = partnerId ? `?partnerId=${partnerId}` : '';

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const { data: tradingOfferOptionsData, actions: tradingOfferOptionsActions } =
    useGetTradingOfferOptions({
      tradingOfferId,
      societyId: actualSociety?._id || '',
    });

  const { data: statusOptionsData, actions: statusOptionsActions } =
    useGetTradingOfferOperationStatusOptions({
      tradingOfferOperationsCount: actualSociety?.tradingOfferOperationsCount,
    });

  const {
    data: tradingOfferTypeOptionsData,
    actions: tradingOfferTypeOptionsActions,
  } = useGetTradingOfferTypeOptions({
    tradingOfferOperationsCount: actualSociety?.tradingOfferOperationsCount,
  });

  const {
    data: tradingOfferOperationsData,
    isLoading: isTradingOfferOperationsLoading,
  } = useGetTradingOfferOperations({
    partnerId,
    tradingOfferId: tradingOfferOptionsData.selectedTradingOfferOption?.value,
    societyId: actualSociety?._id || '',
    operationStatus: statusOptionsData.selectedStatusOption?.value,
    tradingOfferType:
      tradingOfferTypeOptionsData.selectedTradingOfferTypeOption?.value,
    page,
    rowsPerPage,
  });

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleViewTradingOfferOperation = (index: number) => {
    const tradingOfferOperation = tradingOfferOperations[index];
    if (!tradingOfferOperation) return;
    history.push(
      `/detalle-operacion-oferta/${actualSociety?._id}/${tradingOfferOperation?._id}${partnerQuery}`
    );
  };

  const handleEditTradingOfferOperationStatus = (index: number) => {
    const tradingOfferOperation = tradingOfferOperations[index];

    if (!tradingOfferOperation) return;

    store.dispatch(
      setModal(
        <EditTradingOfferOperationStatusModalProvider
          tradingOfferOperation={tradingOfferOperation}
        >
          <EditTradingOfferOperationStatusModal />
        </EditTradingOfferOperationStatusModalProvider>
      )
    );
  };

  return (
    <TradingOfferOperationsContext.Provider
      value={{
        data: {
          totalTradingOfferOperations: tradingOfferOperationsData.total || 0,
          tradingOfferOperations: tradingOfferOperations || [],
          selectedTradingOfferOption:
            tradingOfferOptionsData.selectedTradingOfferOption,
          tradingOfferOptions: tradingOfferOptionsData.tradingOfferOptions,
          statusOptions: statusOptionsData.statusOptions,
          tradingOfferTypeOptions:
            tradingOfferTypeOptionsData.tradingOfferTypeOptions,
          isPartnerView: !!partnerId,
          partnerId,
          page,
          rowsPerPage,
          isAdmin,
          isReadOnly: false,
          isLoading: isTradingOfferOperationsLoading,
        },
        actions: {
          handleChangeTradingOfferOption:
            tradingOfferOptionsActions.handleChangeTradingOfferOption,
          handleSearchTradingOffer:
            tradingOfferOptionsActions.handleSearchTradingOffer,
          handleChangeStatusOption: statusOptionsActions.handleChangeOption,
          handleChangeTradingOfferTypeOption:
            tradingOfferTypeOptionsActions.handleChangeOption,
          handleChangePage,
          handleChangeRowsPerPage,
          handleViewTradingOfferOperation,
          handleEditTradingOfferOperationStatus,
        },
      }}
    >
      {children}
    </TradingOfferOperationsContext.Provider>
  );
};

export const useTradingOfferOperations = () => {
  const context = useContext(TradingOfferOperationsContext);
  if (!context) {
    throw new Error(
      'useTradingOfferOperations must be used within a TradingOfferOperationsProvider'
    );
  }
  return context;
};
